import React, { useMemo } from "react";
import {
  Breadcrumbs,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Link,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { Link as RouterLink } from "react-router-dom";

import NavigateNextIcon from "@material-ui/icons/NavigateNext";

import { useHistory } from "react-router";

import { Alert } from "@material-ui/lab";

import Navigation from "../../Navigation";
import useCurrentEnvironment from "../../hooks/use-environment";
import SetUpDomainForm from "./SetUpDomainPage/SetUpDomainForm";
import useCurrentProjectAndEnvNames from "../../hooks/useCurrentProjectAndEnvNames";
import { userIsFPEmployee } from "../../util";

import useListWebTrackerMonitoringSites from "../queries/useListWebTrackerMonitoringSites";

import useWTMURLs from "../useWTMURLs";

const useStyles = makeStyles((theme) => ({
  breadcrumb: {
    marginTop: 5,
    marginBottom: 10,
  },
  card: {
    width: "100%",
    maxWidth: 800,
    position: "relative",
  },
  cardContent: {
    padding: "1em",
  },
  cardHeader: {
    backgroundColor: theme.palette.grey[50],
    borderBottomColor: theme.palette.grey[200],
    borderBottomStyle: "solid",
    borderBottomWidth: 1,
    padding: theme.spacing(2),
  },
  cardNav: {
    padding: theme.spacing(2),
  },
  loader: {
    left: "50%",
    position: "absolute",
    top: "50%",
    transform: "translateX(-50%) translateY(-50%)",
  },
  navigation: {
    paddingTop: 12,
  },
  pageTitle: {
    paddingTop: 12,
  },
}));

function SetUpDomainPage() {
  const classes = useStyles();
  const history = useHistory();
  const wtmURLs = useWTMURLs();

  const currentEnvironment = useCurrentEnvironment();
  const {
    data: existingWTMSites,
    isLoading: existingWTMSitesLoading,
  } = useListWebTrackerMonitoringSites();

  const isLoading = !currentEnvironment.loaded || existingWTMSitesLoading;

  const {
    isLoading: envNamesLoading,
    projName,
    envName,
  } = useCurrentProjectAndEnvNames();

  const existingWTMSite = useMemo(() => {
    if (
      !currentEnvironment ||
      currentEnvironment.ID === undefined ||
      existingWTMSites === undefined
    ) {
      return undefined;
    }

    return existingWTMSites.find(
      (site) => site.envID === currentEnvironment.ID
    );
  }, [currentEnvironment, existingWTMSites]);

  if (!userIsFPEmployee()) {
    history.push(wtmURLs.trackersOverview);
    return null;
  }

  return (
    <Navigation>
      <div className={classes.pageTitle}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          className={classes.breadcrumb}
        >
          <RouterLink color="inherit" to={wtmURLs.trackersOverview}>
            <Typography variant="overline">Web Tracker Manager</Typography>
          </RouterLink>
          <Typography variant="overline">Set Up Domain</Typography>
        </Breadcrumbs>
        {isLoading && <Typography variant="h3">Loading...</Typography>}

        {!envNamesLoading && projName && envName && (
          <Typography component="h1" gutterBottom variant="h3">
            Set Up Web Tracker Monitoring for {projName} &gt; {envName}
          </Typography>
        )}
      </div>

      <Card className={classes.card}>
        <CardHeader
          className={classes.cardHeader}
          title="Set Up Web Tracker Monitoring"
        />

        <Divider />

        <CardContent className={classes.cardContent}>
          {isLoading && <CircularProgress className={classes.loader} />}

          {!isLoading && existingWTMSite !== undefined && (
            <>
              <Alert severity="info">
                <Typography variant="body1">
                  Web Tracker Monitoring is already set up for this environment
                  for the domain <strong>{existingWTMSite.domain}</strong>.
                  <br />
                  <Link>
                    <RouterLink to={wtmURLs.trackersOverview}>
                      Click here
                    </RouterLink>
                  </Link>{" "}
                  to see this environment's Web Tracker Monitoring page.
                </Typography>
              </Alert>
            </>
          )}

          {!isLoading && existingWTMSite === undefined && <SetUpDomainForm />}
        </CardContent>
      </Card>
    </Navigation>
  );
}

export default SetUpDomainPage;
